import { FormControl } from '@angular/forms';
import { IBaseModel } from './base.model';
import { IQueryParams } from './queryparams';
import { IClient } from './clients.models';
import { IInsurer } from './insurer.model';
import { IPolicy } from './policy';
import { IIntermediary } from './intermediary.model';
import {
  ICardDetails,
  PaymentMethod,
  PaymentStatus,
  PaymentType,
  WalletTransferPortion,
} from './payment.model';
import { IBankDetail } from './broker.model';
import { CommissionStatuses, IIntermediaryCommission } from './commission';
import { IPartner } from './partners.models';
import { Currency } from './currency';

export interface KyfResponse {
  StatusCode: number;
  first_name: string;
  last_name: string;
}

export interface IPaymentModeData {
  paymentMethod: PaymentMethod;
  bankDetail: IBankDetail | null;
  mobileNumber: string | null;
  cardDetails: ICardDetails | null;
}


export interface IBankDetailForm {
  accountNumber: FormControl<string>;
  accountName: FormControl<string>;
  name: FormControl<string>;
  swiftCode: FormControl<string>;
  branch: FormControl<string>;
}

export interface IMobileMoneyForm {
  accountNumber: FormControl<string>;
}

export type RequisitionStatus =
  | 'Pending'
  | 'AwaitingSettlement'
  | 'Settled'
  | 'SettlementFailed'
  | 'PendingApproval'
  | 'Approved'
  | 'Rejected'
  | 'PaymentProcessing';
export type PaymentRequisitionType =
  | 'Cancellation'
  | 'Claim'
  | 'Commission'
  | 'Revision'
  /** Unearned premium owed back on a life scheme — raised by an early settlement or a member exit. */
  | 'PremiumRefund'
  /** A second new-business payment taken for an already-issued policy, paid back; the policy stays in force. */
  | 'DuplicatePayment';

/** An extra new-business payment on a policy paid more than once (GET requisitions/duplicate-payments). */
export interface IDuplicatePayment {
  transactionId: number;
  policyId: number;
  policyType: string;
  policyNumber?: string;
  clientName?: string;
  insurerName?: string;
  intermediaryName?: string;
  amount: number;
  paymentMethod: string;
  billedAccount?: string;
  status: string;
  paidAt: string;
  keptTransactionId?: number;
  keptPaidAt?: string;
  keptBilledAccount?: string;
  /** Premium already paid over to the insurer; it has to be recovered from them. */
  settledToInsurer: boolean;
  /** Mobile money, so the refund flow can pay it back. */
  refundable: boolean;
  requisitionId?: number;
  requisitionNumber?: string;
  requisitionStatus?: RequisitionStatus;
}

export interface IDuplicatePaymentStats {
  count: number;
  amount: number;
  notRaisedCount: number;
  notRaisedAmount: number;
}

export interface IDuplicatePaymentParams {
  pageNumber: number;
  pageSize: number;
  policyNumber?: string;
  refundableOnly: boolean;
  insurerId?: number | null;
}

export const RequisitionStatuses: RequisitionStatus[] = [
  'Pending',
  'AwaitingSettlement',
  'Settled',
  'SettlementFailed',
  'PendingApproval',
  'Approved',
  'Rejected',
  'PaymentProcessing',
];

export type PaymentRequisitionHistoryStatus =
  | 'Created'
  | 'PendingApproval'
  | 'Approved'
  | 'Rejected'
  | 'Revised'
  | 'AwaitingSettlement'
  | 'Settled'
  | 'SettlementFailed'
  | 'PaymentProcessing';

export interface IPaymentRequisitionHistoryEntry extends IBaseModel {
  status: PaymentRequisitionHistoryStatus;
  actorId: number;
  actorName: string;
  reason?: string | null;
}

export interface IPaymentRequisitions extends IBaseModel {
  policy: IPolicy;
  paymentMode: string | null;
  accountNumber: string | null;
  accountName: string | null;
  bankName: string | null;
  swiftCode: string | null;
  paymentRequisitionStatus: RequisitionStatus;
  paymentRequisitionType: PaymentRequisitionType;
  paymentNumber: string;
}

export interface IPaymentRequisitionParams extends IQueryParams {
  statuses?: RequisitionStatus[];
  commissionStatus: CommissionStatuses[];
  types?: PaymentRequisitionType[];
  query?: string;
  intermediaryId?: number | null;
  partnerId?: number | null;
  insurerId?: number | null;
  policyId?: number | null;
  policyNumber?: string | null;
  clientId?: number | null;
}

/**
 * The scheme behind a payout requisition. Present instead of `policy` on a scheme refund or claim —
 * those have no policy, so this is what names the payee and what is being paid for.
 */
export interface IRequisitionScheme {
  schemeId: number;
  schemeName: string;
  policyNumber?: string | null;
  product?: string | null;
  corporateClientName?: string | null;
  corporateClientEmail?: string | null;
  corporateClientPhoneNumber?: string | null;
  corporateClientPostalAddress?: string | null;
  corporateClientContactPerson?: string | null;
}

export interface IRequisition extends IBaseModel {
  policyId?: number;
  policy?: IPolicy;
  scheme?: IRequisitionScheme | null;
  intermediaryId?: number;
  intermediary?: IIntermediary | null;
  insurer?: IInsurer | null;
  partner?: IPartner;
  payment?: IVoucherPaymentResponse;
  paymentMethod?: PaymentMethod;
  bankDetail?: IBankDetail;
  mobileNumber: string;
  paymentRequisitionStatus: RequisitionStatus;
  paymentRequisitionType: PaymentRequisitionType;
  paymentNumber: string;
  amount: number;
  narration?: string;
  payee: string;
  commissionStatuses: string;
  intermediaryCommissions?: IIntermediaryCommission[];
  createdById?: number;
  history: IPaymentRequisitionHistoryEntry[];
}

export interface IRequisitionListItem {
  id: number;
  paymentNumber: string;
  paymentRequisitionType: PaymentRequisitionType;
  intermediaryName?: string | null;
  intermediaryEmail?: string | null;
  clientName?: string | null;
  /** Set on a scheme premium refund, where there is no policy to name the payout by. */
  schemeName?: string | null;
  schemeId?: number | null;
  amount: number;
  paymentRequisitionStatus: RequisitionStatus;
  createdAt: string;
}

export interface IRequisitionStats {
  pendingApprovalCount: number;
  awaitingSettlementCount: number;
  awaitingSettlementAmount: number;
  settledCount: number;
  settledAmount: number;
}

export interface IVoucherPaymentResponse extends IBaseModel {
  pxnId: string;
  amount: number;
  paymentMethod?: PaymentMethod;
  paymentType: PaymentType;
  paymentGateway: string;
  status: PaymentStatus;
  billedAccount: string;
  narration: string;
  intermediaryId?: number;
  intermediary?: IIntermediary;
  insurerId?: number;
  insurer?: IInsurer;
  clientId?: number;
  client?: IClient;
  partnerId?: number;
  partner?: IPartner;
  currency: Currency;
  proofOfPaymentUrl?: string;
  errorMessage?: string;
  /** Set on internal wallet transfers. */
  transferPortion?: WalletTransferPortion | null;
  /** Internal wallet transfers only: whether an admin may retry it now, per the backend's retry rule. */
  retryable?: boolean;
  /** A retry only re-checks it with Lipila, which already acknowledged it. */
  retryChecksStatusOnly?: boolean;
}

export interface INarrationRequest {
  paymentRequisitionId?: number;
  narration: string;
  proofOfPaymentUrl?: string | null;
}

export interface INarrationForm {
  narration: FormControl<string>;
}

export interface ICommissionPaymentAmountForm {
  amount: FormControl<number>;
}
